import { deleteCookiesByPrefix, loadScript, removeScript } from '@/shared/lib/analytics/scriptLoader'
import {
  configString,
  type AnalyticsEventProps,
  type AnalyticsProvider,
  type AnalyticsProviderConfig,
} from '@/shared/lib/analytics/types'

const SCRIPT_ID = 'cse-analytics-ga4'
const GTAG_ORIGIN = 'https://www.googletagmanager.com/gtag/js'
const COOKIE_PREFIXES = ['_ga', '_gid', '_gat'] as const

function ensureGtag(): NonNullable<Window['gtag']> {
  window.dataLayer ??= []
  if (window.gtag === undefined) {
    const dataLayer = window.dataLayer
    // gtag.js only recognises the native `arguments` object, not a rest array.
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      dataLayer.push(arguments)
    }
  }

  return window.gtag
}

export function createGa4Provider(): AnalyticsProvider {
  return {
    key: 'ga4',
    load(config: AnalyticsProviderConfig): void {
      const measurementId = configString(config, 'measurement_id')
      const gtag = ensureGtag()
      gtag('consent', 'default', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
      })
      gtag('js', new Date())
      gtag('config', measurementId, {
        send_page_view: false,
        anonymize_ip: true,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      })
      gtag('consent', 'update', { analytics_storage: 'granted' })
      loadScript(SCRIPT_ID, `${GTAG_ORIGIN}?id=${encodeURIComponent(measurementId)}`).catch(
        () => undefined,
      )
    },
    unload(): void {
      window.gtag?.('consent', 'update', { analytics_storage: 'denied' })
      removeScript(SCRIPT_ID)
      delete window.gtag
      delete window.dataLayer
      deleteCookiesByPrefix(COOKIE_PREFIXES)
    },
    pageview(path: string): void {
      window.gtag?.('event', 'page_view', {
        page_path: path,
        page_location: `${window.location.origin}${path}`,
        page_title: document.title,
      })
    },
    track(name: string, props?: AnalyticsEventProps): void {
      window.gtag?.('event', name, props)
    },
  }
}
